import { api, BASE_URL } from './api-client';

type SuccessEnvelope<T> = { data: T };

export type PrivacyRequestType = 'deletion' | 'export';
export type PrivacyRequestStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed';

export type PrivacyRequest = {
  id: string;
  organization_id: string;
  request_type: PrivacyRequestType;
  status: PrivacyRequestStatus;
  regulation: string | null;
  retrieval_window: string | null;
  subject_input: { distinct_ids: string[] };
  matched_event_count: number | null;
  requested_by: string | null;
  requested_via: string;
  error: string | null;
  created_at: string;
  completed_at: string | null;
  download_available: boolean;
};

export type PreflightResult = {
  resolved: {
    identityIds: string[];
    userIds: string[];
    anonymousIds: string[];
  };
  matchedEventCount: number;
};

export type CreatePrivacyRequestInput = {
  request_type: PrivacyRequestType;
  distinct_ids: string[];
  regulation?: string | null;
  window?: 'all' | 'trailing_12_months' | null;
};

export const PrivacyRequestService = {
  // Org-scoped list (the admin's active org), kept consistent with create /
  // preflight / download, which are all org-scoped, so the page never mixes a
  // cross-org list with org-scoped destructive actions. (Review H1.)
  list: async (): Promise<PrivacyRequest[]> => {
    const result =
      await api.get<SuccessEnvelope<PrivacyRequest[]>>('/privacy-requests');
    return result.data;
  },

  // Platform-admin cross-org oversight list (read-only). Super-admin only;
  // returns rows from every organization, each carrying `organization_id`.
  listAllRequests: async (): Promise<PrivacyRequest[]> => {
    const result = await api.get<SuccessEnvelope<PrivacyRequest[]>>(
      '/platform/privacy-requests',
    );
    return result.data;
  },

  // Resolve ids + count matching events before a destructive confirm (org-scoped).
  preflight: async (distinctIds: string[]): Promise<PreflightResult> => {
    const result = await api.post<SuccessEnvelope<PreflightResult>>(
      '/privacy-requests/preflight',
      { distinct_ids: distinctIds },
    );
    return result.data;
  },

  create: async (
    input: CreatePrivacyRequestInput,
  ): Promise<{ id: string; status: string }> => {
    const result = await api.post<
      SuccessEnvelope<{ id: string; status: string }>
    >('/privacy-requests', input);
    return result.data;
  },

  // Export download is a file (gunzipped JSON), not the JSON envelope the api
  // client parses, so fetch it raw with credentials and trigger a browser save.
  download: async (id: string): Promise<void> => {
    const response = await fetch(`${BASE_URL}/privacy-requests/${id}/download`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dsar-${id}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
